import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import SEO from '@/components/SEO';
import StructuredData from '@/components/StructuredData';
import HeroSection from '@/components/HeroSection';
import SectionHeading from '@/components/SectionHeading';
import CTASection from '@/components/CTASection';
import { Card, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import NotFound from '@/pages/NotFound';
import {
  Shield, Lock, Clock, Eye, Zap, Users, FileCheck, Award, Globe2, Server,
  AlertTriangle, Search, RefreshCw, Layers, Database, Network, Mail,
  TrendingUp, Radar, CheckCircle2, Check
} from 'lucide-react';

const ICONS = {
  Shield, Lock, Clock, Eye, Zap, Users, FileCheck, Award, Globe2, Server,
  AlertTriangle, Search, RefreshCw, Layers, Database, Network, Mail,
  TrendingUp, Radar, CheckCircle2
};

const Icon = ({ name, className }) => {
  const Cmp = ICONS[name] || Shield;
  return <Cmp className={className} />;
};

const PartnerSolution = () => {
  const { slug } = useParams();
  const [entries, setEntries] = useState(null);

  useEffect(() => {
    fetch(`${process.env.PUBLIC_URL}/data/partner-solutions.json`)
      .then((res) => res.json())
      .then((data) => setEntries(data))
      .catch((err) => console.error('Error loading partner solutions:', err));
  }, []);

  if (entries === null) return null;

  const data = entries.find((e) => e.slug === slug);
  if (!data) return <NotFound />;

  const { meta, hero, partnership, whyMatters, productBands, pillars, cta, vendorName } = data;

  return (
    <>
      <SEO
        title={meta.title}
        description={meta.description}
        keywords={meta.keywords}
        url={`/partners/${slug}`}
        ogTitle={meta.ogTitle}
        ogDescription={meta.ogDescription}
        twitterTitle={meta.twitterTitle}
        twitterDescription={meta.twitterDescription}
      />
      <StructuredData type="Product" data={{
        name: meta.structuredDataName,
        brand: { '@type': 'Brand', name: vendorName },
        description: meta.structuredDataDescription,
        url: `https://gulfdigital-me.com/partners/${slug}`,
        manufacturer: { '@type': 'Organization', name: vendorName },
        offeredBy: { '@type': 'Organization', name: 'Gulf Digital', url: 'https://gulfdigital-me.com' }
      }} />

      <HeroSection
        eyebrow={hero.badge}
        title={hero.title}
        subtitle={hero.subtitle}
        primaryCTA={{ text: 'Request a Demo', href: '/contact' }}
        secondaryCTA={{ text: 'Talk to Our Team', href: '/contact' }}
      >
        {hero.complianceTags?.length > 0 && (
          <div className="flex flex-wrap items-center justify-center gap-3 pt-8 border-t border-warm-gray-200">
            <span className="text-sm text-muted-foreground mr-1">Solutions help you align with:</span>
            {hero.complianceTags.map((tag) => (
              <span key={tag} className="px-4 py-2 bg-white rounded-lg shadow-sm text-sm font-semibold text-warm-gray-700">
                {tag}
              </span>
            ))}
          </div>
        )}
      </HeroSection>

      {/* Partnership intro */}
      <section className="py-20 bg-white" data-testid="partnership-section">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-[1.1fr_0.9fr] gap-12 items-start">
            <div>
              <h2 className="text-2xl md:text-3xl font-bold mb-5">{partnership.heading}</h2>
              {partnership.paragraphs.map((p, i) => (
                <p key={i} className="text-muted-foreground mb-4 leading-relaxed">{p}</p>
              ))}
            </div>
            <div className="space-y-4">
              {partnership.stats.map((stat, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.1 }}
                  className="bg-white border border-warm-gray-200 rounded-2xl p-7"
                >
                  <div className="text-xl font-bold text-brand-maroon mb-1">{stat.label}</div>
                  <div className="text-sm text-muted-foreground">{stat.caption}</div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Why it matters */}
      <section className="py-20 bg-warm-gray-50" data-testid="why-matters-section">
        <div className="container-custom">
          <SectionHeading
            eyebrow={whyMatters.eyebrow}
            title={whyMatters.heading}
            description={whyMatters.description}
            titleColor="foreground"
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {whyMatters.cards.map((card, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
              >
                <Card className="h-full border-warm-gray-200">
                  <CardHeader>
                    <div className="w-11 h-11 rounded-lg bg-brand-maroon/10 flex items-center justify-center mb-4">
                      <Icon name={card.icon} className="w-5 h-5 text-brand-maroon" />
                    </div>
                    <CardTitle className="text-lg">{card.title}</CardTitle>
                    <CardDescription>{card.text}</CardDescription>
                  </CardHeader>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Product bands */}
      {productBands.map((band, i) => (
        <section key={i} className={`py-20 ${i % 2 === 0 ? 'bg-white' : 'bg-warm-gray-50'}`} data-testid={`product-band-${i}`}>
          <div className="container-custom">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="bg-white border border-warm-gray-200 rounded-2xl p-8 md:p-10"
            >
              {band.tag && (
                <span className="inline-block text-xs font-bold uppercase tracking-wider text-brand-maroon bg-brand-maroon/10 px-3 py-1.5 rounded-full mb-4">
                  {band.tag}
                </span>
              )}
              <h3 className="text-2xl md:text-3xl font-bold mb-2">{band.title}</h3>
              {band.tagline && <p className="text-muted-foreground mb-6 max-w-3xl">{band.tagline}</p>}

              {band.checklist?.length > 0 && (
                <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {band.checklist.map((item, idx) => (
                    <li key={idx} className="flex items-start gap-2.5 text-sm">
                      <Check className="w-4 h-4 text-brand-maroon flex-shrink-0 mt-0.5" />
                      <span>
                        <strong className="font-semibold">{item.strong}</strong>
                        {item.text && <span className="text-muted-foreground"> — {item.text}</span>}
                      </span>
                    </li>
                  ))}
                </ul>
              )}

              {band.grid?.length > 0 && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-2">
                  {band.grid.map((item, idx) => (
                    <div key={idx} className="bg-warm-gray-50 rounded-xl p-5">
                      <h4 className="font-bold text-sm mb-1.5">{item.title}</h4>
                      <p className="text-sm text-muted-foreground">{item.text}</p>
                    </div>
                  ))}
                </div>
              )}
            </motion.div>
          </div>
        </section>
      ))}

      {/* Pillars */}
      <section className="py-20 bg-warm-gray-50" data-testid="pillars-section">
        <div className="container-custom">
          <SectionHeading
            eyebrow={pillars.eyebrow}
            title={pillars.heading}
            description={pillars.description}
            titleColor="foreground"
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 text-center">
            {pillars.items.map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
              >
                <div className="w-16 h-16 rounded-full bg-brand-maroon/10 flex items-center justify-center mx-auto mb-4">
                  <Icon name={item.icon} className="w-6 h-6 text-brand-maroon" />
                </div>
                <h3 className="text-lg font-bold mb-2">{item.title}</h3>
                <p className="text-sm text-muted-foreground">{item.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <CTASection
        title={cta.heading}
        description={cta.text}
        primaryCTA={{ text: cta.buttonText, href: '/contact' }}
      />

      <section className="py-10 bg-white text-center">
        <Link to="/partners" className="text-brand-maroon font-medium hover:underline">
          ← Back to all partners
        </Link>
      </section>
    </>
  );
};

export default PartnerSolution;
